import type { Mode } from './types';

/**
 * Regras do Business Tour (fonte: telas e vídeos do jogo, conferidos em 19/10/2026).
 * Valores em milhares de contos: 2.000 = 2 milhões.
 *
 * Confirmado no jogo: 32 casas, 2 milhões iniciais, +300 mil no Início, sair da Ilha por 200 mil,
 * Volta ao Mundo por 50 mil, compra de cidade alheia pelo dobro do valor, Campeonato x2 → x3,
 * resort de 200 mil com aluguel 25/50/100 mil, casa 1 e casa 2 custando o mesmo que o terreno,
 * casa 3 um pouco mais cara, imposto de 10% do valor das propriedades, eliminação na falência,
 * vitória por tempo (mais rico), 3 grupos, linha inteira ou 4 resorts.
 * Calibrados no simulador (o jogo não publica): aluguéis por nível, preço do hotel, venda ao banco.
 */

/** Versão das regras. Uma sala fica travada na versão em que começou. */
export const RULES_VERSION = '2.0.0';

export const BOARD_SIZE = 32;
export const START_SQUARE = 0;
export const ISLAND_SQUARE = 8;
export const CHAMPIONSHIP_SQUARE = 16;
export const WORLD_TOUR_SQUARE = 24;

/**
 * Todos começam com o mesmo dinheiro. A ordem de jogada é sorteada no início ("Sorteando quem
 * começa…"), então cada pessoa tem a mesma chance de ser a primeira (decisão do Matheus em 20/10:
 * ninguém reclama de ter começado com menos).
 */
export const START_CASH = 2000;
export const PASS_START_BONUS = 300;
export const JAIL_FINE = 200;
export const MAX_JAIL_ATTEMPTS = 3;
export const WORLD_TOUR_COST = 50;
/** Cartas na mão (guardadas + compradas na lojinha), iguais ou não (pedido do Matheus em 20/10). */
export const HAND_LIMIT = 3;
/**
 * Lojinha: cartas que dá para comprar na sua vez, antes de rolar os dados. Preço em mil contos.
 * advogado = Habeas Corpus; fazendeiro = expulsa a próxima invasão do MST de graça;
 * contador = livra do próximo Faz o L.
 */
export const SHOP: Record<'advogado' | 'fazendeiro' | 'seguro' | 'cupom' | 'contador', number> = {
  advogado: 300,
  fazendeiro: 250,
  seguro: 200,
  cupom: 150,
  contador: 250,
};
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 4;

/** Preço do terreno (bandeirola) por grupo de cor, do mais barato ao mais caro. */
export const GROUP_PRICES: Record<number, number> = { 1: 60, 2: 90, 3: 120, 4: 150, 5: 190, 6: 230, 7: 280, 8: 350 };
export const RESORT_PRICE = 200;
/** Aluguel do resort conforme quantos resorts o dono tem (com 4 ele vence na hora). */
export const RESORT_RENTS = [25, 50, 100, 200] as const;

/**
 * Custo de cada nível, como no Business Tour (ex.: Hamburgo, terreno 120 mil: casas de
 * 50 mil cada e hotel de 150 mil, total 420 mil). Casa = 5/12 do terreno; hotel = 3 casas.
 */
export const HOUSE_COST_SHARE = 5 / 12;
export const HOTEL_HOUSES = 3;
/** Aluguel por nível, em múltiplos de P: bandeirola, casa 1, casa 2, casa 3, hotel (Hamburgo com casa 2: 150 mil). */
export const LEVEL_RENT_MULT = [0.2, 0.6, 1.25, 2.5, 4.5] as const;
/** Grupo completo dobra o aluguel. */
export const MONOPOLY_MULT = 2;
/**
 * "Evento": no início da partida, 2 cidades sorteadas ganham uma fachada especial e cobram
 * 1,7 vez o aluguel normal, pelo mesmo preço (pedido do Matheus em 20/10). No código o nome
 * antigo continua `festivals`.
 */
export const FESTIVAL_MULT = 1.7;
export const FESTIVAL_COUNT = 2;
/** O Campeonato começa em x2 e sobe 1 a cada vez que a mesma cidade é escolhida de novo. */
export const CHAMPIONSHIP_START_MULT = 2;
export const CHAMPIONSHIP_MAX_MULT = 5;
/** Comprar a cidade de outro jogador custa 4 vezes o valor investido nela (pedido do Matheus em 20/10). */
export const TAKEOVER_MULT = 4;
/** Vender ao banco (para pagar dívida) devolve metade do valor investido. */
export const SELL_RATE = 0.5;
/** "Faz o L": imposto pesado sobre o valor das propriedades (pedido do Matheus em 20/10). */
export const TAX_RATE = 0.25;
/**
 * Sonegação: na casa do imposto, quem sonega joga os dados. Soma 8 ou mais (5/12 de chance)
 * paga só 20%; senão paga o dobro e vai para a cadeia sem poder sair com dupla, com fiança
 * tripla. Na média não compensa: é uma aposta.
 */
export const EVADE_MIN_TOTAL = 8;
export const EVADE_SUCCESS_SHARE = 0.2;
export const EVADE_FAIL_MULT = 2;
/** Tentativas na cadeia do sonegador: na 4ª vez sai (3 rodadas paradas, contra 2 na Ilha normal). */
export const EVADE_JAIL_ATTEMPTS = 4;
export const EVADE_JAIL_FINE_MULT = 3;

/**
 * Invasão do MST (pedido do Matheus em 20/10): cidade com casa que passa MST_IDLE_ROUNDS rodadas
 * (12: calibrado na simulação para dar umas 5 invasões por partida; com 5 eram mais de 30)
 * sem ninguém pagar aluguel nela é invadida e perde um nível (casa 3 → 2 → 1 → bandeirola; nunca
 * menos que a bandeirola; hotel ninguém mexe). O dono pode chamar o fazendeiro na hora, pagando
 * MST_EXPEL_COST (bem caro). A Carta Fazendeiro protege um terreno para sempre.
 */
export const MST_IDLE_ROUNDS = 12;
export const MST_EXPEL_COST = 300;

/** Na compra dá para ir da bandeirola até a casa 3; o hotel exige já ter a casa 3 e cair de novo na cidade. */
export const MAX_LEVEL_BEFORE_HOTEL = 3;
export const HOTEL_LEVEL = 4;

/**
 * Sorte de Malandro: quem não sai gastando tudo (tem pelo menos uma propriedade e guarda em caixa
 * metade do que já investiu) ou é o azarão (tem no máximo metade das propriedades da média dos
 * outros) fica com o trevo aceso. Ao puxar carta com o trevo, numa parte das
 * vezes ele olha as 2 de cima e fica com a melhor (ataque > Legaaaal > Caos > F total); a outra
 * continua no topo do baralho.
 */
export const CLOVER_CASH_SHARE = 0.5;
export const CLOVER_CHANCE = 0.6;
export const CLOVER_PEEK = 2;
export const UNDERDOG_SHARE = 0.5;
export const UNDERDOG_MIN_AVG = 2;
/**
 * Ímã do Azarão: as cidades do azarão puxam quem para na casa logo antes delas. Quem para ali
 * joga um dado especial: 50% de chance de ser puxado (pedido do Matheus em 20/10).
 */
export const MAGNET_CHANCE = 0.5;

/** Limites da "Facada" (só efeito visual): aluguel ≥ 25% do dinheiro, ou maior que o maior aluguel que o pagador cobra. */
export const FACADA_CASH_SHARE = 0.25;
export const FACADA_MIN = 100;

export interface ModeRules {
  durationMs: number;
}

const MIN = 60_000;

export const MODES: Record<Mode, ModeRules> = {
  relampago: { durationMs: 15 * MIN },
  padrao: { durationMs: 30 * MIN },
  classico: { durationMs: 60 * MIN },
};

/** Faixas de tempo (fração da partida) em que cada nível de tensão começa (só visual; Caos entra no 3). */
export const TENSION_THRESHOLDS = [0, 0.25, 0.5, 0.8] as const;
export const TENSION_ACCEL = 0.1;
export const TENSION_MAX_ACCEL = 0.15;
/** A tensão corre num relógio de no máximo 20 min (partidas do Business Tour raramente passam disso). */
export const TENSION_REF_MS = 20 * MIN;
